import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { resourcesApi } from '../api/resources'
import { Resource, CheckChannelResponse, PricingTiersResponse } from '../types'

export const useResources = (ownerId?: string): UseQueryResult<Resource[], Error> => {
  return useQuery({
    queryKey: ['resources', ownerId],
    queryFn: () => resourcesApi.listResources(ownerId),
    enabled: !!ownerId,
    staleTime: 3 * 60 * 1000, // 3 minutes
  })
}

export const useResource = (resourceId?: string): UseQueryResult<Resource, Error> => {
  return useQuery({
    queryKey: ['resource', resourceId],
    queryFn: () => {
      if (!resourceId) throw new Error('Resource ID is required')
      return resourcesApi.getResource(resourceId)
    },
    enabled: !!resourceId,
    staleTime: 3 * 60 * 1000,
  })
}

export const useCreateResource = (): UseMutationResult<Resource, Error, Partial<Resource>> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resourcesApi.createResource,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['resources', data.owner_id] })
      // Invalidate user cache to reflect updated resource_retention balance
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

export const useUpdateResource = (): UseMutationResult<Resource, Error, { resourceId: string; data: Partial<Resource> }> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resourceId, data }) => resourcesApi.updateResource(resourceId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['resource', data.id] })
      queryClient.invalidateQueries({ queryKey: ['resources', data.owner_id] })
      // Invalidate user cache to reflect updated resource_retention balance
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

export const useDeleteResource = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resourcesApi.deleteResource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      // Invalidate user cache to reflect updated resource_retention balance
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

export const useCheckChannel = (): UseMutationResult<CheckChannelResponse, Error, string> => {
  return useMutation({
    mutationFn: (link: string) => resourcesApi.checkResource(link),
  })
}

export const useCheckResource = (): UseMutationResult<CheckChannelResponse, Error, { link?: string; botToken?: string }> => {
  return useMutation({
    mutationFn: ({ link, botToken }) => resourcesApi.checkResource(link, botToken),
  })
}

export const useUpdateResourceStatus = (): UseMutationResult<Resource, Error, { resourceId: string; status: 'active' | 'inactive' | 'pending' | 'rejected' | 'maintenance' }> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resourceId, status }) => resourcesApi.updateResourceStatus(resourceId, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['resource', data.id] })
      queryClient.invalidateQueries({ queryKey: ['resources', data.owner_id] })
      // Invalidate user cache to reflect updated resource_retention balance
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

export const useToggleResource = (): UseMutationResult<Resource, Error, { resourceId: string; newStatus: 'active' | 'inactive' }> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resourceId, newStatus }) => resourcesApi.toggleResource(resourceId, newStatus),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['resource', data.id] })
      queryClient.invalidateQueries({ queryKey: ['resources', data.owner_id] })
      // Invalidate user cache to reflect updated resource_retention balance
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

export const useGetPricingTiers = (resourceId?: string): UseQueryResult<PricingTiersResponse, Error> => {
  return useQuery({
    queryKey: ['pricing-tiers', resourceId],
    queryFn: () => {
      if (!resourceId) throw new Error('Resource ID is required')
      return resourcesApi.getPricingTiers(resourceId)
    },
    enabled: !!resourceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useChangeResourcePrice = (): UseMutationResult<Resource, Error, { resourceId: string; price: number }> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ resourceId, price }) => resourcesApi.changeResourcePrice(resourceId, price),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['resource', data.id] })
      queryClient.invalidateQueries({ queryKey: ['resources', data.owner_id] })
      queryClient.invalidateQueries({ queryKey: ['pricing-tiers', data.id] })
      // Invalidate user cache to reflect potential resource_retention changes
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}
